import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  ArrowLeft, User, Mail, Phone, MapPin, Edit, Save, X,
  Globe, Facebook, Instagram, Linkedin, Twitter, Lock, Shield
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CustomerProfile() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Passcode-related state
  const [showPasscodeDialog, setShowPasscodeDialog] = useState(false);
  const [passcodeDialogType, setPasscodeDialogType] = useState(''); // 'set', 'verify', 'change', 'remove'
  const [passcodeForm, setPasscodeForm] = useState({
    password: '',
    passcode: '',
    confirmPasscode: ''
  });
  const [processingPasscode, setProcessingPasscode] = useState(false);
  
  const [editForm, setEditForm] = useState({
    full_name: '',
    phone_number: '',
    street_address: '',
    city: '',
    state: '',
    country: 'United States',
    website_url: '',
    facebook_url: '',
    instagram_url: '',
    linkedin_url: '',
    twitter_url: ''
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/customer/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfile(response.data);
      
      // Parse old address if exists (backwards compatibility)
      const address = response.data.address || '';
      let street = '', city = '', state = '';
      if (address && !response.data.street_address) {
        const parts = address.split(',').map(p => p.trim());
        street = parts[0] || '';
        city = parts[1] || '';
        state = parts[2] || '';
      }
      
      setEditForm({
        full_name: response.data.full_name || '',
        phone_number: response.data.phone_number || '',
        street_address: response.data.street_address || street,
        city: response.data.city || city,
        state: response.data.state || state,
        country: response.data.country || 'United States',
        website_url: response.data.website_url || '',
        facebook_url: response.data.facebook_url || '',
        instagram_url: response.data.instagram_url || '',
        linkedin_url: response.data.linkedin_url || '',
        twitter_url: response.data.twitter_url || ''
      });
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!editForm.full_name.trim()) {
      toast.error('Full name is required');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/customer/profile`, editForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      await fetchProfile();
      setIsEditing(false);
      toast.success('Profile updated successfully');
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditForm({
      full_name: profile.full_name || '',
      phone_number: profile.phone_number || '',
      street_address: profile.street_address || '',
      city: profile.city || '',
      state: profile.state || '',
      country: profile.country || 'United States',
      website_url: profile.website_url || '',
      facebook_url: profile.facebook_url || '',
      instagram_url: profile.instagram_url || '',
      linkedin_url: profile.linkedin_url || '',
      twitter_url: profile.twitter_url || ''
    });
    setIsEditing(false);
  };

  const handleEditClick = async () => {
    // Check if user has a login passcode set
    if (profile.login_passcode) {
      // Show passcode verification dialog
      setPasscodeDialogType('verify');
      setPasscodeForm({ password: '', passcode: '', confirmPasscode: '' });
      setShowPasscodeDialog(true);
    } else {
      // No passcode set, allow edit directly
      setIsEditing(true);
    }
  };

  // Login Passcode handlers (used for both profile editing and login)
  const handleSetLoginPasscode = () => {
    setPasscodeDialogType('set-login');
    setPasscodeForm({ password: '', passcode: '', confirmPasscode: '' });
    setShowPasscodeDialog(true);
  };

  const handleChangeLoginPasscode = () => {
    setPasscodeDialogType('change-login');
    setPasscodeForm({ password: '', passcode: '', confirmPasscode: '' });
    setShowPasscodeDialog(true);
  };

  const handleRemoveLoginPasscode = () => {
    setPasscodeDialogType('remove-login');
    setPasscodeForm({ password: '', passcode: '', confirmPasscode: '' });
    setShowPasscodeDialog(true);
  };

  // Password change handler
  const handleChangePassword = () => {
    setPasscodeDialogType('change-password');
    setPasscodeForm({ password: '', passcode: '', confirmPasscode: '' });
    setShowPasscodeDialog(true);
  };

  const handlePasscodeSubmit = async () => {
    const token = localStorage.getItem('token');
    
    if (passcodeDialogType === 'verify') {
      // Verify login passcode for profile editing
      if (!passcodeForm.passcode || passcodeForm.passcode.length !== 6) {
        toast.error('Please enter your 6-digit login passcode');
        return;
      }
      
      setProcessingPasscode(true);
      try {
        // Verify by attempting login with passcode
        const response = await axios.post(`${API}/auth/login`, {
          email: profile.email,
          password: passcodeForm.passcode,
          role: 'customer'
        });
        
        setShowPasscodeDialog(false);
        setIsEditing(true);
        toast.success('Passcode verified');
      } catch (error) {
        toast.error('Invalid passcode');
      } finally {
        setProcessingPasscode(false);
      }
    } else if (passcodeDialogType === 'set-login' || passcodeDialogType === 'change-login') {
      // Set or change login passcode
      if (!passcodeForm.password) {
        toast.error('Please enter your account password');
        return;
      }
      if (!passcodeForm.passcode || passcodeForm.passcode.length !== 6) {
        toast.error('Login passcode must be exactly 6 digits');
        return;
      }
      if (!/^\d+$/.test(passcodeForm.passcode)) {
        toast.error('Login passcode must contain only numbers');
        return;
      }
      if (passcodeForm.passcode !== passcodeForm.confirmPasscode) {
        toast.error('Passcodes do not match');
        return;
      }
      
      setProcessingPasscode(true);
      try {
        await axios.post(`${API}/auth/set-login-passcode`, 
          { password: passcodeForm.password, passcode: passcodeForm.passcode },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        await fetchProfile();
        setShowPasscodeDialog(false);
        toast.success(passcodeDialogType === 'set-login' ? 'Login passcode set successfully' : 'Login passcode changed successfully');
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to set login passcode');
      } finally {
        setProcessingPasscode(false);
      }
    } else if (passcodeDialogType === 'remove-login') {
      // Remove login passcode
      if (!passcodeForm.password) {
        toast.error('Please enter your account password');
        return;
      }
      
      setProcessingPasscode(true);
      try {
        await axios.delete(`${API}/auth/remove-login-passcode?password=${encodeURIComponent(passcodeForm.password)}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        await fetchProfile();
        setShowPasscodeDialog(false);
        toast.success('Login passcode removed successfully');
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to remove login passcode');
      } finally {
        setProcessingPasscode(false);
      }
    } else if (passcodeDialogType === 'change-password') {
      // Change password using either current password OR passcode
      const hasPasscode = passcodeForm.passcode && passcodeForm.passcode.length === 6;
      const hasPassword = passcodeForm.password;
      
      if (!hasPasscode && !hasPassword) {
        toast.error('Please enter either your current password or 6-digit passcode');
        return;
      }
      
      if (!passcodeForm.confirmPasscode || passcodeForm.confirmPasscode.length < 6) {
        toast.error('New password must be at least 6 characters');
        return;
      }
      
      setProcessingPasscode(true);
      try {
        // Verify credentials first
        const verifyPayload = {
          email: profile.email,
          password: hasPasscode ? passcodeForm.passcode : passcodeForm.password,
          role: 'customer'
        };
        
        await axios.post(`${API}/auth/login`, verifyPayload);
        
        // Now update password
        await axios.post(`${API}/auth/change-password`, 
          { 
            current_credential: hasPasscode ? passcodeForm.passcode : passcodeForm.password,
            new_password: passcodeForm.confirmPasscode,
            credential_type: hasPasscode ? 'passcode' : 'password'
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        setShowPasscodeDialog(false);
        toast.success('Password changed successfully');
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to change password');
      } finally {
        setProcessingPasscode(false);
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <p className="font-outfit text-gray-600">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream pt-6 pb-12" data-testid="customer-profile-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center mb-6">
          <Button
            onClick={() => navigate('/customer/events')}
            variant="ghost"
            data-testid="back-button"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          
          {!isEditing && (
            <Button
              onClick={handleEditClick}
              className="bg-primary hover:bg-primary/90"
            >
              <Edit className="w-4 h-4 mr-2" />
              Edit Profile
            </Button>
          )}
        </div>

        <h1 className="font-playfair font-bold text-4xl text-primary mb-8">My Profile</h1>

        <Card className="p-8">
          {/* Profile Header */}
          <div className="flex items-center mb-8 pb-8 border-b">
            <div className="w-20 h-20 bg-gradient-to-br from-primary to-emerald-600 rounded-full flex items-center justify-center mr-6">
              <User className="w-10 h-10 text-white" />
            </div>
            <div>
              <h2 className="font-playfair font-bold text-3xl text-primary mb-1">{profile.full_name}</h2>
              <p className="font-outfit text-sm text-gray-500" data-testid="user-id">ID: {profile.user_id}</p>
            </div>
          </div>

          {isEditing ? (
            /* Edit Mode */
            <div className="space-y-6">
              {/* Basic Information */}
              <div>
                <h3 className="font-semibold text-lg mb-4">Basic Information</h3>
                
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="full-name">Full Name *</Label>
                    <Input
                      id="full-name"
                      value={editForm.full_name}
                      onChange={(e) => setEditForm({ ...editForm, full_name: e.target.value })}
                      placeholder="Enter your full name"
                    />
                  </div>

                  <div>
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      value={profile.email}
                      disabled
                      className="bg-gray-100 cursor-not-allowed"
                    />
                    <p className="text-xs text-gray-500 mt-1">Email cannot be changed</p>
                  </div>

                  <div>
                    <Label htmlFor="phone">Phone Number</Label>
                    <Input
                      id="phone"
                      value={editForm.phone_number}
                      onChange={(e) => setEditForm({ ...editForm, phone_number: e.target.value })}
                      placeholder="Enter your phone number"
                    />
                  </div>

                  {/* Address Section */}
                  <div className="space-y-4">
                    <Label className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-gray-600" />
                      Address
                    </Label>
                    
                    <div>
                      <Label htmlFor="street" className="text-sm text-gray-600">Street Address</Label>
                      <Input
                        id="street"
                        value={editForm.street_address}
                        onChange={(e) => setEditForm({ ...editForm, street_address: e.target.value })}
                        placeholder="123 Main Street"
                      />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="city" className="text-sm text-gray-600">City</Label>
                        <Input
                          id="city"
                          value={editForm.city}
                          onChange={(e) => setEditForm({ ...editForm, city: e.target.value })}
                          placeholder="City"
                        />
                      </div>
                      <div>
                        <Label htmlFor="state" className="text-sm text-gray-600">State</Label>
                        <Input
                          id="state"
                          value={editForm.state}
                          onChange={(e) => setEditForm({ ...editForm, state: e.target.value })}
                          placeholder="State"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <Label htmlFor="country" className="text-sm text-gray-600">Country</Label>
                      <Input
                        id="country"
                        value={editForm.country}
                        onChange={(e) => setEditForm({ ...editForm, country: e.target.value })}
                        placeholder="Country"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Social & Online Presence */}
              <div className="border-t pt-6">
                <h3 className="font-semibold text-lg mb-4">Social & Online Presence</h3>
                
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="website" className="flex items-center gap-2">
                      <Globe className="w-4 h-4 text-gray-600" />
                      Website
                    </Label>
                    <Input
                      id="website"
                      value={editForm.website_url}
                      onChange={(e) => setEditForm({ ...editForm, website_url: e.target.value })}
                      placeholder="https://yourwebsite.com"
                    />
                  </div>

                  <div>
                    <Label htmlFor="facebook" className="flex items-center gap-2">
                      <Facebook className="w-4 h-4 text-blue-600" />
                      Facebook
                    </Label>
                    <Input
                      id="facebook"
                      value={editForm.facebook_url}
                      onChange={(e) => setEditForm({ ...editForm, facebook_url: e.target.value })}
                      placeholder="https://facebook.com/yourprofile"
                    />
                  </div>

                  <div>
                    <Label htmlFor="instagram" className="flex items-center gap-2">
                      <Instagram className="w-4 h-4 text-pink-600" />
                      Instagram
                    </Label>
                    <Input
                      id="instagram"
                      value={editForm.instagram_url}
                      onChange={(e) => setEditForm({ ...editForm, instagram_url: e.target.value })}
                      placeholder="https://instagram.com/yourprofile"
                    />
                  </div>

                  <div>
                    <Label htmlFor="linkedin" className="flex items-center gap-2">
                      <Linkedin className="w-4 h-4 text-blue-700" />
                      LinkedIn
                    </Label>
                    <Input
                      id="linkedin"
                      value={editForm.linkedin_url}
                      onChange={(e) => setEditForm({ ...editForm, linkedin_url: e.target.value })}
                      placeholder="https://linkedin.com/in/yourprofile"
                    />
                  </div>

                  <div>
                    <Label htmlFor="twitter" className="flex items-center gap-2">
                      <Twitter className="w-4 h-4 text-blue-400" />
                      Twitter
                    </Label>
                    <Input
                      id="twitter"
                      value={editForm.twitter_url}
                      onChange={(e) => setEditForm({ ...editForm, twitter_url: e.target.value })}
                      placeholder="https://twitter.com/yourprofile"
                    />
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-6 border-t">
                <Button
                  onClick={handleCancel}
                  variant="outline"
                  disabled={saving}
                >
                  <X className="w-4 h-4 mr-2" />
                  Cancel
                </Button>
                <Button
                  onClick={handleSave}
                  className="bg-primary hover:bg-primary/90"
                  disabled={saving}
                >
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </div>
          ) : (
            /* View Mode */
            <div className="space-y-6">
              {/* Basic Information */}
              <div className="space-y-4">
                <div className="flex items-start">
                  <Mail className="w-5 h-5 text-amber-600 mr-4 mt-1" />
                  <div>
                    <p className="font-outfit text-sm text-gray-500">Email</p>
                    <p className="font-outfit text-lg text-gray-800" data-testid="email">{profile.email}</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <Phone className="w-5 h-5 text-amber-600 mr-4 mt-1" />
                  <div>
                    <p className="font-outfit text-sm text-gray-500">Phone Number</p>
                    <p className="font-outfit text-lg text-gray-800" data-testid="phone">
                      {profile.phone_number || 'Not provided'}
                    </p>
                  </div>
                </div>

                {(profile.street_address || profile.city || profile.state || profile.country || profile.address) && (
                  <div className="flex items-start">
                    <MapPin className="w-5 h-5 text-amber-600 mr-4 mt-1" />
                    <div>
                      <p className="font-outfit text-sm text-gray-500">Address</p>
                      <p className="font-outfit text-lg text-gray-800">
                        {profile.street_address && <>{profile.street_address}<br /></>}
                        {profile.city && profile.state && <>{profile.city}, {profile.state}<br /></>}
                        {profile.country && <>{profile.country}</>}
                        {!profile.street_address && profile.address && profile.address}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Social Links */}
              {(profile.website_url || profile.facebook_url || profile.instagram_url || 
                profile.linkedin_url || profile.twitter_url) && (
                <div className="border-t pt-6">
                  <p className="font-outfit text-sm text-gray-500 mb-4">Social & Online Presence</p>
                  <div className="flex flex-wrap gap-3">
                    {profile.website_url && (
                      <a
                        href={profile.website_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 px-4 py-2 bg-gray-50 text-gray-700 rounded-lg hover:bg-gray-100 transition"
                      >
                        <Globe className="w-5 h-5" />
                        <span className="text-sm font-medium">Website</span>
                      </a>
                    )}
                    {profile.facebook_url && (
                      <a
                        href={profile.facebook_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition"
                      >
                        <Facebook className="w-5 h-5" />
                        <span className="text-sm font-medium">Facebook</span>
                      </a>
                    )}
                    {profile.instagram_url && (
                      <a
                        href={profile.instagram_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 px-4 py-2 bg-pink-50 text-pink-700 rounded-lg hover:bg-pink-100 transition"
                      >
                        <Instagram className="w-5 h-5" />
                        <span className="text-sm font-medium">Instagram</span>
                      </a>
                    )}
                    {profile.linkedin_url && (
                      <a
                        href={profile.linkedin_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-800 rounded-lg hover:bg-blue-100 transition"
                      >
                        <Linkedin className="w-5 h-5" />
                        <span className="text-sm font-medium">LinkedIn</span>
                      </a>
                    )}
                    {profile.twitter_url && (
                      <a
                        href={profile.twitter_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-500 rounded-lg hover:bg-blue-100 transition"
                      >
                        <Twitter className="w-5 h-5" />
                        <span className="text-sm font-medium">Twitter</span>
                      </a>
                    )}
                  </div>
                </div>
              )}

              {/* Member Since */}
              <div className="pt-4 border-t">
                <p className="font-outfit text-xs text-gray-500">
                  Member since: {new Date(profile.created_at).toLocaleDateString()}
                </p>
              </div>

              {/* Login Passcode & Security Section */}
              {!isEditing && (
                <div className="pt-4 border-t mt-4">
                  <div className="flex items-start gap-3">
                    <Lock className="w-5 h-5 text-primary mt-1" />
                    <div className="flex-1">
                      <h3 className="font-outfit font-semibold text-gray-900 mb-2">Account Security</h3>
                      <p className="text-sm text-gray-600 mb-4">
                        {profile.login_passcode 
                          ? 'You can log in and edit your profile using your 6-digit passcode. You can also change your password using your passcode.'
                          : 'Set up a 6-digit passcode for quick login and profile editing. You will need your account password to set it up.'
                        }
                      </p>
                      
                      <div className="space-y-3">
                        {/* Login Passcode Management */}
                        <div>
                          <p className="text-sm font-medium text-gray-700 mb-2">Login Passcode</p>
                          <div className="flex flex-wrap gap-2">
                            {!profile.login_passcode ? (
                              <Button
                                onClick={handleSetLoginPasscode}
                                size="sm"
                                className="bg-blue-600 hover:bg-blue-700 text-white"
                              >
                                <Lock className="w-4 h-4 mr-2" />
                                Set Passcode
                              </Button>
                            ) : (
                              <>
                                <Button
                                  onClick={handleChangeLoginPasscode}
                                  size="sm"
                                  variant="outline"
                                >
                                  Change Passcode
                                </Button>
                                <Button
                                  onClick={handleRemoveLoginPasscode}
                                  size="sm"
                                  variant="outline"
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                >
                                  Remove Passcode
                                </Button>
                              </>
                            )}
                          </div>
                        </div>

                        {/* Password Management */}
                        <div className="pt-3 border-t">
                          <p className="text-sm font-medium text-gray-700 mb-2">Account Password</p>
                          <div className="flex flex-wrap gap-2">
                            <Button
                              onClick={handleChangePassword}
                              size="sm"
                              variant="outline"
                            >
                              <Shield className="w-4 h-4 mr-2" />
                              Change Password
                            </Button>
                          </div>
                          <p className="text-xs text-gray-500 mt-2">
                            {profile.login_passcode 
                              ? 'Change password using your current password OR your 6-digit passcode'
                              : 'Change password using your current password'
                            }
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </Card>

        {/* Passcode Dialog */}
        <Dialog open={showPasscodeDialog} onOpenChange={setShowPasscodeDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-outfit">
                {passcodeDialogType === 'verify' && 'Enter Passcode'}
                {passcodeDialogType === 'set-login' && 'Set Login Passcode'}
                {passcodeDialogType === 'change-login' && 'Change Login Passcode'}
                {passcodeDialogType === 'remove-login' && 'Remove Login Passcode'}
                {passcodeDialogType === 'change-password' && 'Change Password'}
              </DialogTitle>
              <DialogDescription className="font-outfit">
                {passcodeDialogType === 'verify' && 'Enter your 6-digit passcode to edit your profile.'}
                {passcodeDialogType === 'set-login' && 'Create a 6-digit passcode for quick login and profile editing.'}
                {passcodeDialogType === 'change-login' && 'Enter your account password and create a new 6-digit passcode.'}
                {passcodeDialogType === 'remove-login' && 'Enter your account password to remove your login passcode.'}
                {passcodeDialogType === 'change-password' && (profile.login_passcode 
                  ? 'Enter your current password OR your 6-digit passcode, then enter your new password.'
                  : 'Enter your current password, then enter your new password.')}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              {passcodeDialogType === 'verify' ? (
                <div>
                  <Label htmlFor="passcode">Login Passcode</Label>
                  <Input
                    id="passcode"
                    type="password"
                    inputMode="numeric"
                    maxLength={6}
                    placeholder="Enter 6-digit passcode"
                    value={passcodeForm.passcode}
                    onChange={(e) => setPasscodeForm({ ...passcodeForm, passcode: e.target.value.replace(/\D/g, '') })}
                    onKeyDown={(e) => e.key === 'Enter' && handlePasscodeSubmit()}
                  />
                </div>
              ) : passcodeDialogType === 'change-password' ? (
                <>
                  {profile.login_passcode ? (
                    <>
                      <div>
                        <Label htmlFor="password">Current Password (Optional)</Label>
                        <Input
                          id="password"
                          type="password"
                          placeholder="Enter your current password"
                          value={passcodeForm.password}
                          onChange={(e) => setPasscodeForm({ ...passcodeForm, password: e.target.value })}
                        />
                      </div>
                      <div className="text-center text-sm text-gray-500">— OR —</div>
                      <div>
                        <Label htmlFor="passcode">6-Digit Passcode (Optional)</Label>
                        <Input
                          id="passcode"
                          type="password"
                          inputMode="numeric"
                          maxLength={6}
                          placeholder="Enter your 6-digit passcode"
                          value={passcodeForm.passcode}
                          onChange={(e) => setPasscodeForm({ ...passcodeForm, passcode: e.target.value.replace(/\D/g, '') })}
                        />
                      </div>
                    </>
                  ) : (
                    <div>
                      <Label htmlFor="password">Current Password</Label>
                      <Input
                        id="password"
                        type="password"
                        placeholder="Enter your current password"
                        value={passcodeForm.password}
                        onChange={(e) => setPasscodeForm({ ...passcodeForm, password: e.target.value })}
                      />
                    </div>
                  )}
                  <div>
                    <Label htmlFor="new-password">New Password</Label>
                    <Input
                      id="new-password"
                      type="password"
                      placeholder="Enter new password (min 6 characters)"
                      value={passcodeForm.confirmPasscode}
                      onChange={(e) => setPasscodeForm({ ...passcodeForm, confirmPasscode: e.target.value })}
                      onKeyDown={(e) => e.key === 'Enter' && handlePasscodeSubmit()}
                    />
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <Label htmlFor="password">Account Password</Label>
                    <Input
                      id="password"
                      type="password"
                      placeholder="Enter your account password"
                      value={passcodeForm.password}
                      onChange={(e) => setPasscodeForm({ ...passcodeForm, password: e.target.value })}
                    />
                  </div>

                  {passcodeDialogType !== 'remove-login' && (
                    <>
                      <div>
                        <Label htmlFor="new-passcode">New Login Passcode (6 digits)</Label>
                        <Input
                          id="new-passcode"
                          type="password"
                          inputMode="numeric"
                          maxLength={6}
                          placeholder="Enter 6 digit passcode"
                          value={passcodeForm.passcode}
                          onChange={(e) => setPasscodeForm({ ...passcodeForm, passcode: e.target.value.replace(/\D/g, '') })}
                        />
                      </div>

                      <div>
                        <Label htmlFor="confirm-passcode">Confirm Passcode</Label>
                        <Input
                          id="confirm-passcode"
                          type="password"
                          inputMode="numeric"
                          maxLength={6}
                          placeholder="Re-enter passcode"
                          value={passcodeForm.confirmPasscode}
                          onChange={(e) => setPasscodeForm({ ...passcodeForm, confirmPasscode: e.target.value.replace(/\D/g, '') })}
                          onKeyDown={(e) => e.key === 'Enter' && handlePasscodeSubmit()}
                        />
                      </div>
                    </>
                  )}
                </>
              )}
            </div>

            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setShowPasscodeDialog(false)}
                disabled={processingPasscode}
              >
                Cancel
              </Button>
              <Button
                onClick={handlePasscodeSubmit}
                disabled={processingPasscode}
                className="bg-primary hover:bg-primary/90"
              >
                {processingPasscode ? 'Processing...' : 'Confirm'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
